// Migration Part 9: the SPA data layer now targets the self-hosted API client
// instead of the Sharetribe Flex SDK. `createInstance` returns the self-hosted
// client (src/util/apiClient.js); value `types` are the dependency-free
// reimplementations (src/util/sdkTypes.js). The former Flex `transit`/`util`
// re-exports are gone (`p9-drop-final`): the app speaks JSON end-to-end.
import { createInstance, types } from './apiClient';

const isServer = () => typeof window === 'undefined';

// Dependency-free replacement for Flex `util.objectQueryString`. Builds the
// Sharetribe image-variant descriptor `key:value;key:value` (e.g.
// `w:600;h:600;fit:crop`). Our self-hosted listings endpoints alias variant
// names statically and ignore this value, but we keep the format for parity.
const objectQueryString = obj =>
  Object.entries(obj)
    .map(([k, v]) => `${k}:${v}`)
    .join(';');

// Flex-free replacement for the former `sharetribe-flex-sdk` `util` surface.
// Only `objectQueryString` is used across the SPA (image-variant descriptors).
const util = { objectQueryString };

// create image variant from variant name, desired width and aspectRatio
const createImageVariantConfig = (name, width, aspectRatio) => {
  let variantWidth = width;
  let variantHeight = Math.round(aspectRatio * width);

  if (variantWidth > 3072 || variantHeight > 3072) {
    if (!isServer) {
      console.error(`Dimensions of custom image variant (${name}) are too high (w:${variantWidth}, h:${variantHeight}).
      Reduce them to max 3072px. https://www.sharetribe.com/api-reference/marketplace.html#custom-image-variants`);
    }

    if (variantHeight > 3072) {
      variantHeight = 3072;
      variantWidth = Math.round(variantHeight / aspectRatio);
    } else if (variantHeight > 3072) {
      variantWidth = 3072;
      variantHeight = Math.round(aspectRatio * variantWidth);
    }
  }

  return {
    [`imageVariant.${name}`]: objectQueryString({
      w: variantWidth,
      h: variantHeight,
      fit: 'crop',
    }),
  };
};

// create aspect-ratio-preserving image variant (no crop) from variant name and desired width
// Uses fit=scale with a square bounding box so the image fits within w×w without being cropped.
const createScaledImageVariantConfig = (name, width) => {
  const w = Math.min(width, 3072);
  return {
    [`imageVariant.${name}`]: objectQueryString({
      w,
      h: w,
      fit: 'scale',
    }),
  };
};

export { createInstance, types, util, createImageVariantConfig, createScaledImageVariantConfig };
